import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { LocalContext } from "context/AuthenticationContext";
import { Component, ContextType } from "react";
import { Location, Params } from "react-router-dom";
import { NextObserver, Observable, Subscription } from "rxjs";
import { BasedObservedViewModel } from "../basedObservedViewModel/BasedObservedViewModel";
import {
  DependencyInjections,
  IModuleInjection,
} from "app/di/DependencyInjections";

export interface IBaseViews {
  token?: string | symbol;
  injectionModule?: IModuleInjection;
  addtionalProps?: unknown;
  sharedVM?:
    | BaseViewModel<Disposable, Disposable>
    | BasedObservedViewModel<Disposable, Disposable, unknown>;
  routeParams?: Readonly<Params<string>>;
  location?: Location<any>;
}

export abstract class BasedObservedViews<
  VM extends BasedObservedViewModel<Disposable, Disposable, unknown>,
  S extends Object,
  SS extends Object,
> extends Component<IBaseViews & S, SS> {
  static contextType = LocalContext;
  context: ContextType<typeof LocalContext>;
  viewModel: VM;
  private readonly ownsViewModel: boolean;
  private viewSubscriptions = new Subscription();

  constructor(props: IBaseViews & S) {
    super(props);
    this.ownsViewModel = !props.sharedVM;

    if (props.sharedVM) {
      this.viewModel = props.sharedVM as unknown as VM;
    } else {
      props.injectionModule?.initialize();
      const token = props.token ?? props.injectionModule?.viewModelToken;
      if (!token) {
        throw new Error(
          "A ViewModel token, InjectionModule with exactly one ViewModel binding, or sharedVM is required.",
        );
      }
      this.viewModel = DependencyInjections.lateResolution<VM>(token);
    }

    this.bindViewModel = this.bindViewModel.bind(this);
  }

  protected bindObservedState(...observables: Observable<unknown>[]) {
    observables.forEach((observable) => {
      const sub = observable.subscribe(() => {
        this.forceUpdate();
      });
      this.viewSubscriptions.add(sub);
    });
  }

  protected subscribeToVM<T>(
    observable: Observable<T>,
    next: (value: T) => void,
  ): void {
    const sub = observable.subscribe(next);
    this.viewSubscriptions.add(sub);
  }

  protected connectToVM<T>(nextObserver: NextObserver<T>, nextValue: T) {
    return nextObserver.next(nextValue);
  }

  protected bindViewModel(): void {
    this.viewModel.context = this.context;
    this.bindObservedState(this.viewModel.publicObservedState);
  }

  protected dispose(): void {}

  protected update(): void {}

  componentDidMount(): void {
    this.bindViewModel();
  }

  override componentDidUpdate(): void {
    this.update();
  }

  componentWillUnmount(): void {
    this.dispose();
    this.viewSubscriptions.unsubscribe();
    if (
      this.ownsViewModel &&
      typeof this.viewModel[Symbol.dispose] === "function"
    ) {
      this.viewModel[Symbol.dispose]();
    }
  }
}
